import { Injectable } from '@angular/core';

@Injectable({
  providedIn: 'root',
})
export class ThrottleService {
  /**
   * Creates a throttled function that only invokes the provided function at most once per specified wait time.
   * @param func The function to throttle
   * @param wait The number of milliseconds to throttle invocations to
   * @param options Configuration object for leading/trailing edge execution
   * @returns A new throttled function
   */
  static throttle<T extends (...args: any[]) => any>(
    func: T,
    wait: number,
    options: { leading?: boolean; trailing?: boolean } = {},
  ): T & { cancel: () => void; flush: () => void } {
    let timeoutId: number | null = null;
    let lastArgs: any[] | null = null;
    let lastThis: any = null;
    let lastCallTime: number | null = null;
    let result: any;

    const leading = 'leading' in options ? !!options.leading : true;
    const trailing = 'trailing' in options ? !!options.trailing : true;

    function invokeFunc(time: number) {
      const args = lastArgs!;
      const thisArg = lastThis;

      lastArgs = lastThis = null;
      lastCallTime = time;
      result = func.apply(thisArg, args);
      return result;
    }

    function shouldInvoke(time: number) {
      if (lastCallTime === null) {
        return true;
      }
      const timeSinceLastCall = time - lastCallTime;
      return timeSinceLastCall >= wait;
    }

    function startTimer(pendingFunc: () => any, wait: number) {
      return window.setTimeout(pendingFunc, wait);
    }

    function cancelTimer() {
      if (timeoutId !== null) {
        clearTimeout(timeoutId);
      }
    }

    function trailingEdge(time: number) {
      timeoutId = null;

      if (trailing && lastArgs) {
        return invokeFunc(time);
      }
      lastArgs = lastThis = null;
      return result;
    }

    const throttled = function (this: any, ...args: any[]) {
      const time = Date.now();
      const isInvoking = shouldInvoke(time);

      lastArgs = args;
      lastThis = this;

      if (isInvoking) {
        if (timeoutId === null) {
          lastCallTime = time;
          if (leading) {
            return invokeFunc(time);
          }
        }
        if (trailing) {
          timeoutId = startTimer(() => trailingEdge(Date.now()), wait);
        }
        return result;
      }
      if (timeoutId === null && trailing) {
        timeoutId = startTimer(() => trailingEdge(Date.now()), wait);
      }
      return result;
    } as T & { cancel: () => void; flush: () => void };

    throttled.cancel = function () {
      cancelTimer();
      lastArgs = lastThis = lastCallTime = null;
    };

    throttled.flush = function () {
      if (timeoutId !== null) {
        trailingEdge(Date.now());
      }
    };

    return throttled;
  }
}
